import styled from '@emotion/styled';
import { ReactNode, useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { mqMin } from 'utils/responsive';

interface MaForBeginnerModalOptions {
    /** ESCキーで閉じる? */
    keyboard?: boolean;
    /** 背景をクリックして閉じる? */
    maskClosable?: boolean;
    /** 領域外・またはesc押下でcloseした際のcallbackイベント */
    onClose?: () => void;
}

/** モーダル背景 */
const ModalBg = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    @supports (height: 100svh) {
        width: 100svw;
        height: 100svh;
    }
    background-color: rgba(0, 0, 0, 0.32);
    opacity: ${(props: { isOpen: boolean }) => (props.isOpen ? 1 : 0)};
    z-index: var(--modal);
    pointer-events: ${(props: { isOpen: boolean }) => (props.isOpen ? 'auto' : 'none')};
    transition: opacity 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
`;

/** モーダル内容 */
const ModalContent = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    width: 95vw;
    background-color: transparent;
    z-index: calc(var(--modal) + 1);
    overflow: visible;

    ${mqMin('SP')} {
        width: 90vw;
    }

    ${mqMin('NARROW')} {
        width: 55rem;
    }
`;

const ModalHeader = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    display: flex;
    justify-content: flex-end;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: #666;
    font-size: 1.5rem;
    line-height: 1;
    transition: color 0.2s;

    &:hover {
        color: #333;
    }
`;

const ModalBody = styled.div`
    width: 100%;
    overflow-y: auto;
    max-height: 90vh;
    border-radius: 15px;
`;

export const useMaForBeginnerModal = () => {
    const [isOpened, setIsOpened] = useState(false);

    // モーダルが開いている間は背景のスクロールを無効にする
    useEffect(() => {
        if (isOpened) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // クリーンアップ関数
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpened]);

    // Portalの外側にイベント伝播させない
    const stopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

    const createModal = useCallback(
        (
            content: ReactNode,
            { keyboard = true, maskClosable = true, onClose = () => {} }: MaForBeginnerModalOptions = {},
        ) => {
            const closeOnEsc = (e: KeyboardEvent) => {
                if (isOpened && e.key === 'Escape') close();
            };

            if (keyboard) {
                document.addEventListener('keydown', closeOnEsc);
            }

            const close = () => {
                setIsOpened(false);
                if (keyboard) document.removeEventListener('keydown', closeOnEsc);
                onClose();
            };

            return createPortal(
                <div role="dialog" aria-modal="true" onClick={stopPropagation}>
                    <ModalBg isOpen={isOpened} onClick={maskClosable ? close : () => {}} />
                    {isOpened && (
                        <ModalContent>
                            <ModalHeader>
                                <CloseButton onClick={close}>×</CloseButton>
                            </ModalHeader>
                            <ModalBody>{content}</ModalBody>
                        </ModalContent>
                    )}
                </div>,
                document.getElementById('root') as HTMLElement,
            );
        },
        [isOpened, stopPropagation],
    );

    return {
        openModal: useCallback(() => setIsOpened(true), []),
        closeModal: useCallback(() => setIsOpened(false), []),
        createModal,
    };
};
